import { useApplicationsQuery } from '@access-cms/api-client';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import { CurrentUser, useAuth } from '../components/auth/AuthContext';

interface ApplicantApplicationAccessState {
  hasActiveApplication: boolean;
  isLoading: boolean;
}

const ApplicantApplicationAccessContext = createContext<ApplicantApplicationAccessState>({
  hasActiveApplication: false,
  isLoading: false,
});

function canAccessApplicantExperience(user: CurrentUser | null): boolean {
  const systemRoles = user?.system_roles ?? [];
  const contractRoles = user?.roles ?? [];
  const isRosterOwner = contractRoles.some((role) => role.role_type === 'ROSTER_OWNER');
  const isApproverOrAdmin =
    systemRoles.some((role) => ['ADMINISTRATOR', 'COR', 'FOREIGN_NATIONAL'].includes(role)) ||
    contractRoles.some((role) => ['COR', 'FEDERAL_APPROVER'].includes(role.role_type));

  return isRosterOwner || !isApproverOrAdmin;
}

/**
 * Loads the current applicant's application state once for all RBAC consumers.
 * The list endpoint is read-only and automatically scopes contractors to their
 * own records. /applications/me is intentionally avoided because it can create
 * a DRAFT application as a side effect.
 */
export function ApplicantApplicationAccessProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const shouldFetchApplicationState = !!user && !isAuthLoading && canAccessApplicantExperience(user);
  const applicationQuery = useApplicationsQuery({
    enabled: shouldFetchApplicationState,
    filters: { limit: 100, page: 1 },
  });

  const value = useMemo<ApplicantApplicationAccessState>(
    () => ({
      hasActiveApplication:
        shouldFetchApplicationState &&
        (applicationQuery.data?.data ?? []).some((application) => application.applicationStatus !== 'DRAFT'),
      isLoading: shouldFetchApplicationState && applicationQuery.isLoading,
    }),
    [applicationQuery.data?.data, applicationQuery.isLoading, shouldFetchApplicationState],
  );

  return (
    <ApplicantApplicationAccessContext.Provider value={value}>{children}</ApplicantApplicationAccessContext.Provider>
  );
}

export function useApplicantApplicationAccess(): ApplicantApplicationAccessState {
  return useContext(ApplicantApplicationAccessContext);
}
